import type { MatchState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { EntityId, TeamIdx } from '../types';
import { pickPassTarget } from '../sim/receiver';
import { relevanceScore } from './relevance';

// ControlRouter (§8.2, §10.5) — the server-only session↔entity map. Sim state holds no controller info;
// this router (plus the CONTROL log records it produces) fully determines who controls which entity at
// every tick. Deterministic and transport-agnostic so it unit-tests on a virtual clock.
//
// Evaluation-pass ordering is normative: explicit SwitchRequests first, then auto sessions ascending by
// sessionSlot; each assignment updates the human-held set immediately; all assignments in a pass share one
// effectiveTick = decisionTick + 1 (never retroactive). Invariant: never two sessions on one entity.

export type ControlMode = 'auto' | 'locked';

export interface SwitchAssignRecord {
  sessionSlot: number;
  entityId: EntityId;
  effectiveTick: number;
  /** D36: ticks after effectiveTick during which the bot still drives the new entity (auto switches only; explicit = 0). */
  handoverTicks: number;
}

/** One controlled interval for a session — a CONTROL log record (§11) once closed. */
export interface ControlInterval {
  sessionSlot: number;
  entityId: EntityId;
  startTick: number;
  endTick: number | null; // null = still open
}

interface SessionCtl {
  sessionSlot: number;
  team: TeamIdx;
  entityId: EntityId;
  mode: ControlMode;
  lastSwitchTick: number;
  pendingRequest: EntityId | 'cycle' | null; // explicit SwitchRequest for the next pass
  frozen: boolean; // a disconnected/unresponsive session — excluded from auto-switching until it resumes
  /** D50: the ARMED pass-follow — the session's own kick named this receiver; control switches WHEN HE
   *  GETS THE BALL ("the moment the other player receives"), never at release. An interception simply
   *  cancels (control never left the passer). */
  followReceiver: EntityId | null;
  followArmTick: number; // a receiver TOUCH at/after this tick counts as received
  followLanded: boolean; // set by noteTouch (tick-exact, D43) — the next evaluate applies the switch
  lastCycleTick: number; // D44: tick of the last Tab-cycle (chain detection)
  cycleIndex: number; // D44: position in the relevance ranking for a rapid re-press chain
}

export class ControlRouter {
  private readonly sessions = new Map<number, SessionCtl>();
  private readonly intervals: ControlInterval[] = [];

  constructor(private readonly cfg: SimConfig) {}

  /** Register a session with its initial entity, opening its first control interval at `startTick`. */
  addSession(sessionSlot: number, team: TeamIdx, entityId: EntityId, mode: ControlMode, startTick: number): SwitchAssignRecord {
    // lastSwitchTick starts far in the past so the quiet period (D36) never blocks the FIRST auto switch
    this.sessions.set(sessionSlot, { sessionSlot, team, entityId, mode, lastSwitchTick: startTick - 1000, pendingRequest: null, frozen: false, followReceiver: null, followArmTick: 0, followLanded: false, lastCycleTick: startTick - 1000, cycleIndex: 0 });
    this.intervals.push({ sessionSlot, entityId, startTick, endTick: null });
    return { sessionSlot, entityId, effectiveTick: startTick, handoverTicks: 0 };
  }

  setMode(sessionSlot: number, mode: ControlMode): void {
    const s = this.sessions.get(sessionSlot);
    if (s) s.mode = mode;
  }

  /** Freeze/unfreeze a session's auto-switching (frozen while its socket is disconnected/unresponsive). */
  setFrozen(sessionSlot: number, frozen: boolean): void {
    const s = this.sessions.get(sessionSlot);
    if (s) s.frozen = frozen;
  }

  /** D43/D50: a ball touch this tick (fed by MatchRunner) — the designated RECEIVER touching the pass,
   *  even for one tick between evals, marks the armed pass-follow as RECEIVED; the next evaluate applies
   *  the switch. D52: a touch by ANYONE ELSE (interceptor, a bot one-touching it onward) CANCELS the arm
   *  instead — eval-time possession checks missed transient claims (a bot can claim and instantly
   *  re-pass inside one eval window), which left the arm alive forever and control stuck on the passer. */
  noteTouch(entity: EntityId, tick: number): void {
    for (const s of this.sessions.values()) {
      if (s.followReceiver === null || s.followLanded || tick < s.followArmTick) continue;
      if (entity === s.followReceiver) s.followLanded = true;
      else if (entity !== s.entityId) s.followReceiver = null; // intercepted — control never left the passer
    }
  }

  /** D50: does any session have a RECEIVED pass-follow waiting to apply? Callers use this to run an
   *  evaluate immediately (off-cadence) so control arrives the moment the receiver takes the ball. */
  hasPendingResolution(): boolean {
    for (const s of this.sessions.values()) if (s.followReceiver !== null && s.followLanded) return true;
    return false;
  }

  /** D58: entities a HUMAN's pass is currently flying toward (armed, unresolved follows). The bot may
   *  keep RUNNING them onto the ball, but their KICKS are suppressed at input assembly — the D57 brain
   *  one-touched the human's pass away on the claim tick, one tick before control arrived ("my player
   *  is controlled by a bot"). The first touch belongs to the incoming human. */
  pendingReceivers(): Set<EntityId> {
    const out = new Set<EntityId>();
    for (const s of this.sessions.values()) if (s.followReceiver !== null) out.add(s.followReceiver);
    return out;
  }

  /** Queue an explicit switch onto `target` (an entityId, or 'cycle' for the next entity), for next evaluate(). */
  requestSwitch(sessionSlot: number, target: EntityId | 'cycle'): void {
    const s = this.sessions.get(sessionSlot);
    if (s) s.pendingRequest = target;
  }

  /** D44: Tab = smart switch. A FRESH press (or one after a > CYCLE_CHAIN_TICKS gap) jumps to rank 0 of
   *  the §8.2 relevance ranking (goal-side defender nearest the ball when defending, intercept bonus, GK
   *  sunk by its penalty unless the ball is in our own box) — the useful player, not merely the nearest.
   *  A rapid re-press advances the index through the ranking. Ties break by lower entity id (stable). */
  private nextCycleTarget(state: MatchState, s: SessionCtl, held: ReadonlySet<EntityId>, decisionTick: number): EntityId | null {
    const ranked = state.players
      .filter((p) => p.team === s.team && !p.sentOff && (p.id === s.entityId || !held.has(p.id)))
      .map((p) => ({ id: p.id, score: relevanceScore(state, p, this.cfg) }))
      .sort((a, c) => c.score - a.score || a.id - c.id)
      .map((r) => r.id);
    if (ranked.length === 0) return null;
    const chained = decisionTick - s.lastCycleTick <= this.cfg.switching.CYCLE_CHAIN_TICKS;
    s.lastCycleTick = decisionTick;
    if (chained) {
      s.cycleIndex = (s.cycleIndex + 1) % ranked.length;
    } else {
      // fresh press → best candidate; if that's already the controlled entity, step to the runner-up
      s.cycleIndex = ranked[0] === s.entityId && ranked.length > 1 ? 1 : 0;
    }
    return ranked[s.cycleIndex] ?? null;
  }

  // D18 §8.2 event-driven auto-switch state: last tick's possession, for edge detection
  private prevPossessor: EntityId | null = null;
  /** D123 — edge detection for the restart award (see evaluate step 2b). */
  private prevTaker: EntityId | null = null;
  private prevRestartTeam: TeamIdx | null = null;
  private static readonly PROTECTED = new Set(['TACKLE_STAND', 'TACKLE_SLIDE', 'SLIDE_RECOVERY', 'KICK_WINDUP', 'GK_SMOTHER', 'GK_DIVE']);

  /** D18/D36/D38 auto policy: PASS-FOLLOW of the session's OWN kick — and nothing else. Possession
   *  gains, keeper holds, loose balls, opponent turnovers: all Tab territory now (designer: "we only
   *  switch if it's a pass; any other occasion, no switch until I press Tab"). */
  private pickAutoD18(state: MatchState, s: SessionCtl, held: ReadonlySet<EntityId>, decisionTick: number): { pick: EntityId | null; consumed: boolean } {
    // D36 post-switch quiet period: after ANY switch, the auto rule stays silent for H_COOLDOWN so
    // control never bounces right after a switch (e.g. a kick fired on the very next tick would
    // immediately yank control elsewhere).
    //
    // D134: returning early here used to be FREE, because `evaluate` advanced `prevPossessor` on every
    // call regardless — so the possession-loss edge this rule keys on was CONSUMED by a pass that
    // happened to land inside the cooldown or during a PROTECTED action, and the follow switch then
    // never happened at all. That is silent-no-switch case (2) of the report: the player reads it as
    // "the bot kept control". The edge is now only consumed when it is acted on.
    if (decisionTick - s.lastSwitchTick <= this.cfg.switching.H_COOLDOWN) return { pick: null, consumed: false };
    const me = state.players[s.entityId];
    if (!me || ControlRouter.PROTECTED.has(me.action)) return { pick: null, consumed: false }; // action protection (D)
    const b = state.ball;
    const possChanged = b.possessor !== this.prevPossessor;

    // pass-follow: MY controlled entity just released a REAL KICK (D43: kickedBy — knock-on dribble
    // touches and blocks never count as passes) ⇒ control the receiver. D49: the guess is the SAME
    // selection the kick's soft-lock used (pickPassTarget on the ball's flight direction) — control
    // goes passer → the designated receiver, NEVER a third "inferred" player.
    //
    // D134: the arm no longer requires the ball to still be LOOSE at eval time. It did, and a crisp
    // ground pass claimed between two cadence ticks therefore armed nothing — silent-no-switch case (3).
    // The discriminator that matters is `kickedBy`, which is set at the kick and survives the claim.
    if (b.kickedBy === s.entityId && (possChanged || b.possessor === null) && this.prevPossessor === s.entityId) {
      const speed = Math.hypot(b.vel.x, b.vel.y);
      const from = b.possessor !== null ? state.players[b.possessor]!.pos : b.pos;
      if (speed > 6 || b.possessor !== null) {
        const yaw = speed > 1e-6 ? Math.atan2(b.vel.y, b.vel.x) : 0;
        // D155: a pass-follow follows a PASS, and a pass goes to a team-mate. The `b.possessor` shortcut
        // ("whoever holds it now IS the receiver") had no team check, while the Tab cycle and the explicit
        // request both filter `p.team === s.team`; `held` only rejects entities another SESSION owns. So an
        // opponent intercepting your pass — or their keeper catching your shot — became your new player.
        const guess = b.possessor !== null && b.possessor !== s.entityId ? b.possessor : pickPassTarget(state, s.entityId, { x: from.x, y: from.y }, yaw, Math.max(speed, 7), this.cfg)?.id ?? null;
        const gp = guess !== null ? state.players[guess] : undefined;
        if (gp && gp.team === s.team && !gp.sentOff && guess !== s.entityId && !held.has(guess!)) return { pick: guess, consumed: true };
      }
    }
    return { pick: null, consumed: possChanged };
  }

  /** Entities currently held by a human session. */
  humanHeld(): Set<EntityId> {
    const held = new Set<EntityId>();
    for (const s of this.sessions.values()) held.add(s.entityId);
    return held;
  }

  entityFor(sessionSlot: number): EntityId | null {
    return this.sessions.get(sessionSlot)?.entityId ?? null;
  }

  /** All control intervals so far (open intervals have endTick === null) — the CONTROL record source. */
  controlIntervals(): readonly ControlInterval[] {
    return this.intervals;
  }

  /**
   * The entity a session's input for `tick` should drive (§13.2 switch rebase / T-NET-008): the entity from
   * that session's control interval covering `tick`. Returns null when `tick` predates the earliest retained
   * interval (`retentionFloor` ticks before `nowTick`) — such late inputs are dropped, never misrouted.
   */
  routeInput(sessionSlot: number, tick: number, nowTick: number, retentionFloor = 128): EntityId | null {
    if (tick < nowTick - retentionFloor) return null; // older than retained history ⇒ drop
    let entity: EntityId | null = null;
    let bestStart = -1;
    for (const iv of this.intervals) {
      if (iv.sessionSlot !== sessionSlot) continue;
      if (iv.startTick <= tick && (iv.endTick === null || tick < iv.endTick) && iv.startTick >= bestStart) {
        bestStart = iv.startTick;
        entity = iv.entityId;
      }
    }
    return entity;
  }

  /** The session controlling `entityId` at `tick`, or null (from interval history). */
  controllerAt(entityId: EntityId, tick: number): number | null {
    let owner: number | null = null;
    for (const iv of this.intervals) {
      if (iv.entityId !== entityId) continue;
      if (iv.startTick <= tick && (iv.endTick === null || tick < iv.endTick)) owner = iv.sessionSlot;
    }
    return owner;
  }

  /**
   * Run one evaluation pass at `decisionTick` (§8.2). Explicit requests first, then auto sessions ascending
   * by slot; the human-held set updates after each assignment so two sessions never land on one entity.
   * Returns the SwitchAssign records (empty if nothing moved) — all sharing effectiveTick = decisionTick + 1.
   */
  evaluate(state: MatchState, decisionTick: number): SwitchAssignRecord[] {
    const effectiveTick = decisionTick + 1;
    const held = this.humanHeld();
    const out: SwitchAssignRecord[] = [];
    const slots = [...this.sessions.keys()].sort((a, b) => a - b);

    const apply = (s: SessionCtl, target: EntityId, handoverTicks: number): void => {
      if (target === s.entityId) return;
      s.followReceiver = null; // any switch supersedes an armed pass-follow (D50)
      s.followLanded = false;
      held.delete(s.entityId);
      // close the previous interval, open the new one, reset cooldown, record the assign
      for (const iv of this.intervals) if (iv.sessionSlot === s.sessionSlot && iv.endTick === null) iv.endTick = effectiveTick;
      this.intervals.push({ sessionSlot: s.sessionSlot, entityId: target, startTick: effectiveTick, endTick: null });
      s.entityId = target;
      s.lastSwitchTick = decisionTick;
      held.add(target);
      out.push({ sessionSlot: s.sessionSlot, entityId: target, effectiveTick, handoverTicks });
    };

    // 1 · explicit requests first (in slot order)
    for (const slot of slots) {
      const s = this.sessions.get(slot)!;
      const req = s.pendingRequest;
      s.pendingRequest = null;
      if (req === null) continue;
      const target = req === 'cycle' ? this.nextCycleTarget(state, s, held, decisionTick) : req;
      if (target === null) continue;
      const p = state.players[target];
      // a valid manual pick: own team, on pitch, not held by another session
      // explicit picks are instant — the player asked for this entity NOW (D36: no bot handover)
      if (p && p.team === s.team && !p.sentOff && (target === s.entityId || !held.has(target))) apply(s, target, 0);
    }

    // 2 · D50 pass-follow resolution: an ARMED follow applies WHEN THE RECEIVER GETS THE BALL — he
    // holds it now, or touched it since arming (noteTouch, tick-exact). An interception / a dead ball
    // simply cancels: control never left the passer, so there is nothing to revert and no third player
    // to jump to.
    for (const slot of slots) {
      const s = this.sessions.get(slot)!;
      if (s.followReceiver === null) continue;
      const rcv = s.followReceiver;
      const poss = state.ball.possessor;
      const lt = state.ball.lastTouch;
      const received = s.followLanded || poss === rcv || (lt !== null && lt.entity === rcv && lt.tick >= s.followArmTick);
      if (received) {
        s.followReceiver = null;
        s.followLanded = false;
        const rp = state.players[rcv];
        if (rp && !rp.sentOff && !held.has(rcv)) apply(s, rcv, 0); // instant — no handover window (D49)
      } else if (poss !== null || state.restart !== null) {
        s.followReceiver = null; // intercepted / collected by someone else / out ⇒ stay with the passer
        s.followLanded = false;
      }
    }

    // 2b · D123 restart award: route the KICKING team's session onto the taker, once, on the tick the
    // restart is designated. W_TAKER already makes him reachable by Tab; this means you don't have to
    // notice a free kick was given before you can take it. Deliberately narrow — auto sessions only,
    // kicking team only, and only while a BOT holds the taker: it must never rip an entity out of
    // another human's hands, and a defending session is never moved at all. Edge-triggered on the
    // designation, so a player who switches away during the pending window is not dragged back.
    const r = state.restart;
    const takerNow = r !== null ? r.taker : null;
    const awarded = takerNow !== null && (this.prevTaker === null || this.prevTaker !== takerNow || this.prevRestartTeam !== r!.team);
    if (this.cfg.switching.RESTART_AUTO === 1 && awarded && !held.has(takerNow)) {
      for (const slot of slots) {
        const s = this.sessions.get(slot)!;
        if (s.mode !== 'auto' || s.frozen) continue;
        if (s.team !== r!.team) continue;
        if (s.entityId === takerNow) break; // already his — nothing to do, and nobody else should take him
        apply(s, takerNow, 0); // instant: the player asked for a restart by winning it
        break;
      }
    }
    this.prevTaker = takerNow;
    this.prevRestartTeam = r !== null ? r.team : null;

    let edgeActed = false;
    let autoSessions = 0;
    // 3 · auto sessions, ascending by slot. D50: the session's own kick ARMS a follow onto the kick's
    // designated receiver; the switch itself waits for the reception (step 2, next evaluate — callers
    // poll hasPendingResolution() to run it the moment noteTouch lands).
    for (const slot of slots) {
      const s = this.sessions.get(slot)!;
      if (s.mode !== 'auto' || s.frozen) continue; // locked/frozen sessions don't auto-switch
      autoSessions += 1;
      const { pick, consumed } = this.pickAutoD18(state, s, held, decisionTick);
      if (consumed) edgeActed = true;
      if (pick !== null) {
        // D112 (HB-P6): a HIGH ball resolves its follow at LAUNCH, not at the receiver's touch. A cross
        // hangs 40+ ticks; waiting for the touch would teleport the human into the header on the last
        // tick, with no chance to attack the delivery. Ground passes keep touch-resolution — there the
        // wait is what makes the switch land exactly as the ball arrives (D50).
        const b = state.ball;
        const lofted = Math.atan2(b.vel.z, Math.hypot(b.vel.x, b.vel.y)) >= this.cfg.actions.kick.HIGH.MIN_ELEV;
        if (lofted && this.cfg.switching.AIR_FOLLOW === 1) {
          const rp = state.players[pick];
          if (rp && !rp.sentOff && !held.has(pick)) apply(s, pick, 0);
          continue;
        }
        // D134 (✅ user: "control must swap the moment the other player takes possession"): if the
        // receiver ALREADY has it — a crisp ground pass claimed before this evaluation ran — there is
        // nothing left to wait for. Arming here and applying on the next pass is the same one-tick
        // dither the workstream exists to remove. A DEAD ball is excluded, exactly as step 2's cancel
        // excludes it: a pass that ends in a restart did not complete, so there is nobody to follow.
        if (state.ball.possessor === pick && state.restart === null) {
          const rp = state.players[pick];
          if (rp && !rp.sentOff && !held.has(pick)) apply(s, pick, 0);
          continue;
        }
        s.followReceiver = pick;
        s.followArmTick = effectiveTick;
        s.followLanded = false;
      }
    }

    // D134: advance the edge ONLY when it was acted on (or when no auto session could have used it).
    // Consuming it unconditionally is what dropped follows under H_COOLDOWN and PROTECTED actions.
    if (edgeActed || autoSessions === 0) this.prevPossessor = state.ball.possessor;
    return out;
  }
}
